/** Signable Documents Setup — lease + guarantor packets with include rules. */

import type { QualifyingMethodId } from './conditionalApprovals'
import { QUALIFYING_METHOD_META } from './conditionalApprovals'
import { WORKBOOK_FIELD_CATALOG } from '../ApplicationWorkbook/workbookCatalog'

export type PacketRuleAction = 'include' | 'exclude'

export type PacketWhenKind = 'always' | 'applicant_data' | 'qualifying_method'

export type LeasePacketRule =
    | { type: 'always'; action: PacketRuleAction }
    | {
          type: 'when_answer'
          action: PacketRuleAction
          fieldId: string
          equals: string
      }
    | {
          type: 'when_qualifying_method'
          action: PacketRuleAction
          methodId: QualifyingMethodId
      }

export type LeasePacketMember = {
    id: string
    docId: string | null
    rule: LeasePacketRule
}

export type SignableDocSetDefinition = {
    lease_packet: LeasePacketMember[]
    guarantor_packet: LeasePacketMember[]
}

export const PACKET_CONDITION_FIELDS = WORKBOOK_FIELD_CATALOG.filter(
    (field) =>
        field.input_type !== 'file' &&
        field.id !== 'qualifying_method' &&
        field.id !== 'fallback_qualifying_method',
)

export function packetFieldLabel(fieldId: string): string {
    return (
        PACKET_CONDITION_FIELDS.find((field) => field.id === fieldId)?.label ??
        fieldId
    )
}

export function packetFieldAnswerChoices(fieldId: string): string[] | null {
    const field = PACKET_CONDITION_FIELDS.find((row) => row.id === fieldId)
    if (field?.input_type === 'boolean') return ['Yes', 'No']
    return null
}

export function emptyLeasePacket(): LeasePacketMember[] {
    return []
}

export function emptyGuarantorPacket(): LeasePacketMember[] {
    return []
}

export function emptySignableDocSetDefinition(): SignableDocSetDefinition {
    return {
        lease_packet: emptyLeasePacket(),
        guarantor_packet: emptyGuarantorPacket(),
    }
}

export function packetReady(packet: LeasePacketMember[]): boolean {
    return packet.some(
        (member) =>
            !!member.docId &&
            member.rule.type === 'always' &&
            member.rule.action === 'include',
    )
}

export function leasePacketReady(leasePacket: LeasePacketMember[]): boolean {
    return packetReady(leasePacket)
}

export function signableDocSetReady(
    leasePacket: LeasePacketMember[],
    _guarantorPacket?: LeasePacketMember[],
): boolean {
    return leasePacketReady(leasePacket)
}

export function signablePacketsReadiness(
    leasePacket: LeasePacketMember[],
    guarantorPacket: LeasePacketMember[] = [],
): string {
    const leaseCount = leasePacket.filter((m) => !!m.docId).length
    const guarantorCount = guarantorPacket.filter((m) => !!m.docId).length
    if (!leasePacketReady(leasePacket)) {
        return leaseCount === 0
            ? 'Add an always-include document to the lease packet'
            : 'Lease packet needs at least one always-include document'
    }
    if (guarantorCount === 0) {
        return `Lease packet ready · ${leaseCount} doc${
            leaseCount === 1 ? '' : 's'
        }`
    }
    return `Lease packet ready · ${leaseCount} lease · ${guarantorCount} guarantor`
}

function newPacketMemberId(): string {
    const rand =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
            : `${Date.now().toString(36)}`
    return `pkt_${rand}`
}

export function createLeasePacketMember(
    partial?: Partial<LeasePacketMember>,
): LeasePacketMember {
    return {
        id: partial?.id || newPacketMemberId(),
        docId: partial?.docId ?? null,
        rule: partial?.rule ?? { type: 'always', action: 'include' },
    }
}

export function ruleWhenKind(rule: LeasePacketRule): PacketWhenKind {
    if (rule.type === 'when_answer') return 'applicant_data'
    if (rule.type === 'when_qualifying_method') return 'qualifying_method'
    return 'always'
}

export function describeLeasePacketRule(rule: LeasePacketRule): string {
    const verb = rule.action === 'exclude' ? 'Exclude' : 'Include'
    if (rule.type === 'always') return `${verb} always`
    if (rule.type === 'when_answer') {
        return `${verb} when ${packetFieldLabel(rule.fieldId)} = ${rule.equals}`
    }
    const methodLabel =
        QUALIFYING_METHOD_META[rule.methodId]?.label ?? rule.methodId
    return `${verb} when method = ${methodLabel}`
}

function normalizePacketMember(raw: unknown): LeasePacketMember | null {
    if (!raw || typeof raw !== 'object') return null
    const row = raw as Record<string, unknown>
    const docRaw = row.docId ?? row.doc_id
    const docId = docRaw == null || docRaw === '' ? null : String(docRaw)
    return createLeasePacketMember({
        id: typeof row.id === 'string' ? row.id : undefined,
        docId,
        rule: normalizePacketRule(row.rule),
    })
}

function normalizePacketRule(raw: unknown): LeasePacketRule {
    const action: PacketRuleAction =
        raw &&
        typeof raw === 'object' &&
        (raw as Record<string, unknown>).action === 'exclude'
            ? 'exclude'
            : 'include'
    if (!raw || typeof raw !== 'object') {
        return { type: 'always', action }
    }
    const row = raw as Record<string, unknown>
    if (row.type === 'when_answer') {
        const fieldId = String(row.fieldId ?? row.field_id ?? '')
        const equals = String(row.equals ?? '')
        if (fieldId && equals) {
            return { type: 'when_answer', action, fieldId, equals }
        }
    }
    if (row.type === 'when_qualifying_method') {
        const methodId = String(
            row.methodId ?? row.method_id ?? '',
        ) as QualifyingMethodId
        if (methodId) {
            return { type: 'when_qualifying_method', action, methodId }
        }
    }
    return { type: 'always', action }
}

function normalizePacket(raw: unknown): LeasePacketMember[] {
    if (!Array.isArray(raw)) return []
    return raw
        .map((item) => normalizePacketMember(item))
        .filter((m): m is LeasePacketMember => m != null)
}

function alwaysIncludeFromLegacy(docId: string, id: string): LeasePacketMember {
    return createLeasePacketMember({
        id,
        docId,
        rule: { type: 'always', action: 'include' },
    })
}

/** Normalize pack / API definition into FE draft shape. */
export function normalizeSignableDocSetDefinition(
    raw: Record<string, unknown> | null | undefined,
): SignableDocSetDefinition {
    const base = emptySignableDocSetDefinition()
    if (!raw || typeof raw !== 'object') return base

    base.lease_packet = normalizePacket(raw.lease_packet)
    base.guarantor_packet = normalizePacket(raw.guarantor_packet)

    const roleBindingsIn = raw.role_bindings as
        | Record<string, string | number | null>
        | undefined
    if (roleBindingsIn && typeof roleBindingsIn === 'object') {
        if (base.lease_packet.length === 0) {
            const contract =
                roleBindingsIn.lease_contract ?? roleBindingsIn.lease_package
            if (contract != null && contract !== '') {
                base.lease_packet = [
                    alwaysIncludeFromLegacy(
                        String(contract),
                        'pkt_lease_contract',
                    ),
                ]
            }
        }
        if (base.guarantor_packet.length === 0) {
            const guarantor = roleBindingsIn.guarantor_agreement
            if (guarantor != null && guarantor !== '') {
                base.guarantor_packet = [
                    alwaysIncludeFromLegacy(String(guarantor), 'pkt_guarantor'),
                ]
            }
        }
    }
    return base
}
